import React from "react";
import {
  HandThumbDownIcon,
  HandThumbUpIcon,
  MinusCircleIcon,
  QuestionMarkCircleIcon,
  RocketLaunchIcon,
  CursorArrowRaysIcon,
  BackspaceIcon,
  UserCircleIcon
} from "@heroicons/react/24/outline";
import {FormattedMessage, IntlShape} from "react-intl";
import {VisibilityLevel} from "../Analytics.type";
import Select from "../../../ui-components/Select";
import {AllUsersKpiProps, GroupUsersKpiProps, StatusOption, Users, Role, TrendData} from "./Users.type";


export const statusOptions: StatusOption[] = [
  {category: 'in', value: 'activated', color: 'ag-600', icon: UserCircleIcon, sumValues: ['activated', 'user', 'adopter', 'promoter']},
  {category: 'in', value: 'user', color: 'ag-700', icon: CursorArrowRaysIcon, sumValues: ['user', 'adopter', 'promoter']},
  {category: 'in', value: 'adopter', color: 'green-700', icon: HandThumbUpIcon, sumValues: ['adopter', 'promoter']},
  {category: 'in', value: 'promoter', color: 'kerakoll-700', icon: RocketLaunchIcon},
  {category: 'in', value: 'churn', color: 'red-700', icon: HandThumbDownIcon},

  {category: 'out', value: 'pending', color: 'yellow-600', icon: QuestionMarkCircleIcon},
  {category: 'out', value: 'never_registered', color: 'gray-700', icon: MinusCircleIcon},
  {category: 'out', value: 'leaving', color: 'gray-700', icon: BackspaceIcon}
];

const statusInList = statusOptions.filter(i => i.category === 'in');
const statusOutList = statusOptions.filter(i => i.category === 'out');


const getTotals = (users: Users) => {
  const currentIn = users.data.filter(i => statusInList.map(j => j.value).includes(i.status))
  const currentOut = users.data.filter(i => statusOutList.map(j => j.value).includes(i.status))
  const totalIn = currentIn.reduce((acc, i) => acc + i.val_users, 0)
  const totalOut = currentOut.reduce((acc, i) => acc + i.val_users, 0)
  return {totalIn, totalOut}
}

const getStatusValue = (users: Users, value: string, sumValues?: string[]) => {
  const listValues = sumValues ?? [value]
  const current = users.data.filter(i => listValues.includes(i.status))
  return current.length ? current.reduce((acc, i) => acc + i.val_users, 0) : 0
}

export function AllUsersKpi(
  {
    allUsers
  }: AllUsersKpiProps) {

  const {totalIn, totalOut} = getTotals(allUsers)
  return (
    <div key="all" className="px-4 py-5 xl:p-6 xl:col-span-2 border">
      <dt className="mb-3 text-base font-bold text-black-500">All Users</dt>
      <dd className="-mt-2 flex items-baseline justify-center xl:block xl:flex">
        <div className="flex flex-col items-center">
          <div className="flex items-baseline text-2xl font-semibold">
            {totalIn}{" "}
            <span className="text-xs italic ml-2 font-normal lowercase">
              <FormattedMessage id="registered" defaultMessage="Registrati" />
            </span>
          </div>
        </div>
      </dd>
      <dd className="mt-5 flex items-center justify-between xl:block xl:flex">
        {statusInList.map((s) => {
          const val = getStatusValue(allUsers, s.value, s.sumValues)
          return (
            <div key={s.value} className={`flex flex-col items-center text-${s.color}`}>
              <div>
                <s.icon className="w-5 h-5 mx-1"/>
              </div>
              <div className="flex items-center flex-col xl:flex-row sm:text-lg font-semibold">
                <div className="flex flex-col">
                  <div>{val} <span className="text-xxs sm:text-xs italic font-normal">{s.value}</span></div>
                  {totalIn ?
                    <div className="text-xs text-center">{Math.round(val / totalIn * 100)} %</div> :
                    <div className="text-xs text-center">-</div>
                  }
                </div>
              </div>
            </div>
          )
        })}
      </dd>
      <dd className="mt-4 flex items-baseline justify-between xl:block xl:flex opacity-60">
        <div className="flex flex-col xl:flex-row items-baseline text-xl font-semibold">
          {totalOut}{" "}
          <span className="text-xs italic ml-2 font-normal lowercase">
            <FormattedMessage id="not_registered" defaultMessage="Non iscritti" />
          </span>
        </div>
        {statusOutList.map((s) => {
          const current = allUsers.data.filter(i => i.status === s.value)
          const val = current.length ? current[0].val_users : 0
          return (
            <div key={s.value}
                 className={`inline-block align-middle flex flex-col xl:flex-row items-center text-md font-semibold text-${s.color}`}>
              <div className="flex flex-row items-center">
                <s.icon className="w-4 h-4 mx-2"/>
                {val}</div>
              <span className="text-xs italic ml-1 font-normal">{s.value.replace("_", " ")}</span>
            </div>
          )
        })}
      </dd>
    </div>
  )
}


export function GroupUsersKpi(
  {
    name,
    users,
    filterRole,
    setFilterRole,
    options
  }: GroupUsersKpiProps) {

  const {totalIn, totalOut} = getTotals(users)

  return (
    <div>
      <dt className="mb-3 text-base font-bold text-black-500 flex justify-between">
        <div className="capitalize">
          {name} users
        </div>
        {options.length ?
          <div className="ml-3 -mt-3 -mb-3">
            <Select
              label={undefined}
              className={undefined}
              value={filterRole[name]}
              options={options}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFilterRole({...filterRole, [name]: e.target.value});
              }}
            />
          </div>
          : null
        }
      </dt>
      <dd className="-mt-2 flex items-baseline justify-center xl:block xl:flex">
        <div className="flex flex-col items-center">
          <div className="flex items-baseline text-2xl font-semibold">
            {totalIn}{" "}
            <span className="text-xs italic ml-2 font-normal lowercase">
              <FormattedMessage id="registered" defaultMessage="Registrati" />
            </span>
          </div>
        </div>
      </dd>
      <dd className="mt-5 flex items-baseline justify-between xl:block xl:flex">
        {statusInList.map((s) => {
          const val = getStatusValue(users, s.value, s.sumValues)
          return (
            <div key={s.value} className={`flex flex-col items-center text-${s.color}`}>
              <div>
                <s.icon className="w-5 h-5 mx-2"/>
              </div>
              <div className="flex items-center flex-col xl:flex-row text-xl font-semibold">
                <div className="flex flex-col">
                  <div>{val}<span className="text-xs italic ml-1 font-normal">{s.value}</span></div>
                  {totalIn ?
                    <div className="text-xs text-center">{Math.round(val / totalIn * 100)} %</div> :
                    <div className="text-xs text-center">-</div>
                  }
                </div>
              </div>
            </div>
          )
        })}
      </dd>
      <dd className="mt-4 flex items-baseline justify-between xl:block xl:flex opacity-60">
        <div className="flex flex-col xl:flex-row items-baseline text-xl font-semibold">
          {totalOut} <span className="text-xs italic ml-2 font-normal">non iscritti</span>
        </div>
        {statusOutList.map((s) => {
          const current = users.data.filter(i => i.status === s.value)
          const val = current.length ? current[0].val_users : 0
          return (
            <div
              key={s.value}
              className={`inline-block align-middle flex flex-col xl:flex-row items-center text-md font-semibold text-${s.color}`}
            >
              <div className="flex flex-row items-center">
                <s.icon className="w-4 h-4 mx-2"/>
                {val}
              </div>
              <span className="text-xs italic ml-2 font-normal">{s.value.replace("_", " ")}</span>
            </div>
          )
        })}
      </dd>
    </div>
  )
}


export const getRoleOptions = (roles: Role[], visibilityLevel: VisibilityLevel, intl: IntlShape) => {
  let res = ['all']
  if (visibilityLevel < 4) {
    res.push('all_internal_users')
    res = res.concat(roles.filter(i => i.flg_internal).map(i => i.name))
    res.push('all_external_users')
  }
  res = res.concat(roles.filter(i => !i.flg_internal).map(i => i.name))
  return res.map(i => ({value: i, name: intl.formatMessage({id: i})}))
}


export const getStatusOptions = () => {
  const res = ['registrati', 'activated', 'user', 'adopter', 'promoter', 'churn', 'pending', 'all']
  return res.map(i => ({value: i, name: i[0].toUpperCase() + i.slice(1).replace('_', ' ')}))
}

export const getTrendChart = (data: TrendData[]) => {
  const statusList = statusOptions.map(i => i.value).filter(i => data.map(j => j.status).includes(i))
  const months = Array.from(new Set(data.map(i => i.dat_month))).sort()
  let res: any = []
  months.forEach(m => {
    let row: any = {month: m}
    statusList.forEach(s => {
      const value = data.filter(i => i.dat_month === m && i.status === s)
      row[`val_${s}`] = value.length ? value[0].val_users : 0
    })
    res.push(row)
  })
  return res
}